import { type FC, useCallback, useMemo } from 'react';

import { useTheme } from '@mui/material/styles';
import { Avatar } from '@vakantiesnl/components/src/atoms/Avatar';
import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { hasEnabledFeature } from '@vakantiesnl/components/src/atoms/FeatureToggle/utils';
import { Link } from '@vakantiesnl/components/src/atoms/Link';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { RatingBox } from '@vakantiesnl/components/src/molecules/RatingBox';
import { RatingSubscores } from '@vakantiesnl/components/src/molecules/RatingSubscores';
import { ReviewItem } from '@vakantiesnl/components/src/molecules/ReviewItem';
import { useNewStyles } from '@vakantiesnl/components/src/styles/themes/themewrapper';
import { getSubscores } from '@vakantiesnl/components/src/utils/accommodation/getSubScores';
import { message } from '@vakantiesnl/components/src/utils/message';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import ImageComponent from '@vakantiesnl/image-component';
import { type ContentfulImageParams } from '@vakantiesnl/image-component/src/loaders/contentful';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { useAsset } from '@vakantiesnl/services/src/hooks/contentful';
import { useFeatures } from '@vakantiesnl/services/src/stores';
import { inject } from '@vakantiesnl/services/src/util/template';
import { type Search, type Geo } from '@vakantiesnl/types';
import { type ReviewView } from '@vakantiesnl/types/src/reviews';

import { useCustomStyles } from './Reviews.style';
import { LinedWave } from '../../atoms/LinedWave';

export type ReviewsProps = {
	reviews: ReviewView[];
	reviewCount?: number;
	addReviewUrl?: string;
	onLoadMore: () => void;
} & (
	| {
			type: 'destination';
			destination: Geo.MappedDestinationView;
	  }
	| {
			type: 'accommodation';
			accommodation: Search.FullAccommodationItem;
			AccommodationBanner: () => JSX.Element | undefined;
	  }
);

const contentfulParams: ContentfulImageParams = { fm: 'webp' };

export const Reviews: FC<ReviewsProps> = ({ reviews, reviewCount, addReviewUrl, onLoadMore, ...props }) => {
	const { classes } = useCustomStyles();
	const microCopies = useMicroCopyContext();
	const { isMobile, isDesktop } = useBreakpoints();
	const theme = useTheme();
	const styles = useNewStyles();
	const banner = useAsset(microCopies['review.banner'])?.fields?.file;

	const { name, rating, accommodation, AccommodationBanner } = useMemo(
		() =>
			props.type === 'accommodation'
				? {
						name: props.accommodation.name,
						rating: props.accommodation.rating,
						accommodation: props.accommodation,
						AccommodationBanner: props?.AccommodationBanner,
					}
				: {
						name: props.destination.name,
					},
		[props],
	);

	const featuresState = useFeatures();
	const subscores = accommodation?.reviewPerAspectGroup ? getSubscores(accommodation.reviewPerAspectGroup) : null;

	const showBanner = hasEnabledFeature(featuresState, 'banner-in-review-section') && props.type === 'accommodation';

	const trackClick = useCallback(() => {
		if (!!accommodation) {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ trackWriteReviewClick }) =>
					track(trackWriteReviewClick(accommodation, microCopies['reviews.write'])),
				),
			);
		}
	}, [accommodation, microCopies]);

	if (!reviews.length) {
		return (
			<>
				<LinedWave className={classes.topWave} />
				<div className={classes.section}>
					<div className={classes.container}>
						<div className={classes.headerTop}>
							<Typography
								className={classes.title}
								variant={isMobile ? 'headingLg' : 'headingXl'}
								as="h2"
							>
								{microCopies['reviews.whatVisitorsSaid']}
							</Typography>
							<Typography
								className={classes.accommodationName}
								variant={isMobile ? 'headingXl' : 'heading2Xl'}
								as="h3"
							>
								{name}
							</Typography>
							{showBanner && isDesktop && banner && (
								<ImageComponent
									useLoader="Contentful"
									contentfulParams={contentfulParams}
									className={classes.banner}
									alt="Review banner"
									src={banner.url}
									width={banner.details.image?.width}
									height={banner.details.image?.height}
									sizes={`(min-width:${theme.breakpoints.values.lg}px) ${styles.theme.section.width.lg}px, 100vw`}
								/>
							)}
							<Typography
								className={classes.noReviewsText}
								variant={isMobile ? 'bodyMd' : 'bodyLg'}
								as="p"
							>
								{microCopies['reviews.noReviews.text']}
							</Typography>
							{addReviewUrl && (
								<Link href={addReviewUrl} className={classes.reviewBtn} onClick={trackClick}>
									<Button asText>{microCopies['reviews.write']}</Button>
								</Link>
							)}
						</div>
					</div>
				</div>
				<LinedWave className={classes.bottomWave} isFlipped />
			</>
		);
	}

	return (
		<>
			<LinedWave className={classes.topWave} />
			<div className={classes.section}>
				<div className={classes.container}>
					<div className={classes.header}>
						<div className={classes.headerTop}>
							<Typography
								className={classes.title}
								variant={isMobile ? 'headingLg' : 'headingXl'}
								as="h2"
							>
								{microCopies['reviews.whatVisitorsSaid']}
							</Typography>
							<Typography
								className={classes.accommodationName}
								variant={isMobile ? 'headingXl' : 'heading2Xl'}
								as="h3"
							>
								{name}
							</Typography>
						</div>
						{!!rating && !!reviewCount && (
							<RatingBox
								className={classes.score}
								labelClassName={classes.scoreLabel}
								rating={rating}
								size={isMobile ? 'large' : 'jumbo'}
								microCopies={microCopies}
							>
								<span className={classes.scoreTemplate}>
									{inject(microCopies['ratings.score.template'], {
										reviewCountLink: (
											<span key="accommodation.review" className={classes.scoreReviewCount}>
												{message(microCopies['accommodation.review'], {
													count: reviewCount,
												})}
											</span>
										),
									})}
								</span>
							</RatingBox>
						)}
						{showBanner && isDesktop && banner && (
							<ImageComponent
								useLoader="Contentful"
								contentfulParams={contentfulParams}
								className={classes.banner}
								alt="Review banner"
								src={banner.url}
								width={792}
								height={160}
								/** Keep the aspect ratio of the section width. */
								sizes={`(min-width:${theme.breakpoints.values.lg}px) ${styles.theme.section.width.lg}px, 100vw`}
							/>
						)}
						{addReviewUrl && (
							<div className={classes.block}>
								<Typography
									variant={isDesktop ? 'headingMd' : 'headingXs'}
									as="h4"
									className={classes.writeReviewLeft}
								>
									{!isMobile && <Avatar icon="destination" />}
									{microCopies['reviews.curiousAboutExperience']}
								</Typography>
								<Link href={addReviewUrl} onClick={trackClick} className={classes.reviewBtn}>
									<Button asText>{microCopies['reviews.write']}</Button>
								</Link>
							</div>
						)}
					</div>

					<div className={classes.ratingContainer}>
						<Typography variant="headingLg" as="h4">
							{microCopies['accommodation.menuList.rating']}
						</Typography>
						{accommodation && reviewCount && subscores && <RatingSubscores subscores={subscores} />}
					</div>

					{!!reviewCount && (
						<Typography variant="headingMd" as="h4" className={classes.reviewCount}>
							{inject(microCopies['reviews.countTemplate'], { number: String(reviewCount) })}
						</Typography>
					)}
					<div className={classes.reviews}>
						{reviews.map((review, index) => [
							<ReviewItem key={review.id} review={review} accommodation={accommodation} />,
							// Show book now CTA button every 6 reviews starting from after the third one
							!((index + 4) % 6) && index !== reviews.length - 1 && accommodation && (
								<AccommodationBanner key={index} />
							),
						])}
						{accommodation && <AccommodationBanner />}
					</div>
					{reviews.length < (reviewCount ?? 0) && (
						<Button
							variant="secondary"
							className={classes.loadMore}
							onClick={onLoadMore}
							data-cy="reviews-load-more"
						>
							{microCopies['reviews.loadMore']}
						</Button>
					)}
				</div>
			</div>
			<LinedWave className={classes.bottomWave} isFlipped />
		</>
	);
};
